import React from "react";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  BatteryCharging,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  CloudCog,
  Eye,
  FileText,
  Fuel,
  Gauge,
  Globe2,
  LocateFixed,
  MapPin,
  Navigation,
  Plus,
  Radio,
  RefreshCw,
  Route,
  Search,
  ShieldCheck,
  Sparkles,
  Thermometer,
  Truck,
  UserRound,
  Wrench,
  X,
} from "lucide-react";
import transportService from "../../services/transportService";

const emptyDashboard = {
  metrics: {},
  vehicles: [],
  drivers: [],
  trips: [],
  requests: [],
  maintenance: [],
  fuel_logs: [],
  safety_events: [],
  sensor_readings: [],
  geofences: [],
  integrations: [],
};

const transportSections = [
  { id: "fleet", label: "Fleet", icon: Truck },
  { id: "dispatch", label: "Dispatch", icon: Route },
  { id: "maintenance", label: "Maintenance", icon: Wrench },
  { id: "safety", label: "Safety", icon: ShieldCheck },
  { id: "fuel", label: "Fuel & compliance", icon: Fuel },
  { id: "requests", label: "Requests", icon: ClipboardList },
  { id: "reports", label: "Reports", icon: FileText },
];

const requestTypeOptions = [
  ["STAFF_TRAVEL", "Staff travel"],
  ["STUDENT_ACTIVITY", "Student activity"],
  ["DELIVERY", "Delivery / collection"],
  ["EMERGENCY", "Emergency"],
  ["OTHER", "Other"],
];

const vehicleTypeOptions = [
  ["any", "Any suitable vehicle"],
  ["bus", "Bus"],
  ["van", "Van / minibus"],
  ["pickup", "Pickup / utility"],
  ["saloon", "Saloon / executive"],
];

const fleetPrefix = (vehicleType) =>
  ({
    bus: "BUS",
    van: "VAN",
    pickup: "PUP",
    saloon: "SAL",
  })[String(vehicleType || "").toLowerCase()] || "VEH";

const defaultRequest = {
  requestType: "STAFF_TRAVEL",
  purpose: "",
  origin: "",
  destination: "",
  travelDate: "",
  returnDate: "",
  departureTime: "",
  returnTime: "",
  passengers: 1,
  vehicleType: "any",
  priority: "normal",
  notes: "",
  outsideCampus: false,
};

const defaultVehicle = {
  fleetNumber: "",
  registrationNumber: "",
  makeModel: "",
  vehicleType: "van",
  powertrain: "ICE",
  capacity: 14,
  odometerKm: 0,
  nextServiceDate: "",
  insuranceExpiry: "",
  inspectionExpiry: "",
};

const money = (value) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const dateLabel = (value) =>
  value
    ? new Intl.DateTimeFormat("en-KE", { dateStyle: "medium" }).format(
        new Date(`${String(value).slice(0, 10)}T00:00:00`),
      )
    : "—";

const timeLabel = (value) => (value ? String(value).slice(0, 5) : "—");

const returnTimeLabel = (request) => {
  if (!request.return_time) return "Not specified";
  if (
    String(request.return_date || "").slice(0, 10) ===
      String(request.travel_date || "").slice(0, 10) &&
    timeLabel(request.return_time) === "00:00"
  ) {
    return "Not specified";
  }
  return timeLabel(request.return_time);
};

const dateTimeLabel = (value) =>
  value
    ? new Intl.DateTimeFormat("en-KE", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";

const titleCase = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const MAX_APPROVAL_DOCUMENT_BYTES = 5 * 1024 * 1024;
const APPROVAL_DOCUMENT_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const statusClass = (status) =>
  ({
    AVAILABLE: "border-emerald-200 bg-emerald-50 text-emerald-700",
    ON_TRIP: "border-blue-200 bg-blue-50 text-blue-700",
    MAINTENANCE: "border-amber-200 bg-amber-50 text-amber-700",
    OUT_OF_SERVICE: "border-rose-200 bg-rose-50 text-rose-700",
    OPEN: "border-amber-200 bg-amber-50 text-amber-700",
    REVIEW: "border-blue-200 bg-blue-50 text-blue-700",
    APPROVED: "border-indigo-200 bg-indigo-50 text-indigo-700",
    SCHEDULED: "border-indigo-200 bg-indigo-50 text-indigo-700",
    COMPLETED: "border-emerald-200 bg-emerald-50 text-emerald-700",
    REJECTED: "border-rose-200 bg-rose-50 text-rose-700",
    IN_PROGRESS: "border-violet-200 bg-violet-50 text-violet-700",
  })[String(status || "").toUpperCase()] ||
  "border-slate-200 bg-slate-50 text-slate-600";

const Badge = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${className}`}
  >
    {children}
  </span>
);

const Metric = ({
  label,
  value,
  detail,
  icon: Icon,
  tone = "text-blue-700",
}) => (
  <div className="rounded-xl border bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <Icon className={`h-5 w-5 ${tone}`} />
    </div>
    <p className={`mt-2 text-2xl font-bold ${tone}`}>{value}</p>
    {detail && <p className="mt-1 text-xs text-slate-500">{detail}</p>}
  </div>
);

const EmptyState = ({ icon: Icon = Truck, title, children }) => (
  <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center">
    <Icon className="mx-auto h-8 w-8 text-slate-300" />
    <p className="mt-2 text-sm font-semibold text-slate-700">{title}</p>
    {children && <p className="mt-1 text-xs text-slate-500">{children}</p>}
  </div>
);

const SectionTitle = ({ icon: Icon, title, description, action, leading }) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div className="flex items-start gap-3">
      {leading}
      {Icon && (
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-blue-50 text-blue-700">
          <Icon className="h-5 w-5" />
        </span>
      )}
      <div>
        <h2 className="text-lg font-bold text-slate-950">{title}</h2>
        {description && (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const CapabilityCard = ({ icon: Icon, title, detail, tone = "blue" }) => {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="rounded-xl border bg-white p-4 shadow-sm">
      <span
        className={`grid h-9 w-9 place-items-center rounded-lg ${tones[tone]}`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <p className="mt-3 text-sm font-semibold text-slate-900">{title}</p>
      <p className="mt-1 text-xs leading-5 text-slate-500">{detail}</p>
    </div>
  );
};

const normalizeDashboard = (payload) => ({
  ...emptyDashboard,
  ...(payload || {}),
  metrics: { ...(payload?.metrics || {}) },
  vehicles: Array.isArray(payload?.vehicles) ? payload.vehicles : [],
  drivers: Array.isArray(payload?.drivers) ? payload.drivers : [],
  trips: Array.isArray(payload?.trips) ? payload.trips : [],
  requests: Array.isArray(payload?.requests) ? payload.requests : [],
  maintenance: Array.isArray(payload?.maintenance) ? payload.maintenance : [],
  fuel_logs: Array.isArray(payload?.fuel_logs) ? payload.fuel_logs : [],
  safety_events: Array.isArray(payload?.safety_events)
    ? payload.safety_events
    : [],
  sensor_readings: Array.isArray(payload?.sensor_readings)
    ? payload.sensor_readings
    : [],
  geofences: Array.isArray(payload?.geofences) ? payload.geofences : [],
  integrations: Array.isArray(payload?.integrations)
    ? payload.integrations
    : [],
});

const TransportWorkspace = ({
  notify,
  view = "overview",
  manager = false,
  onViewChange,
  onBack,
}) => {
  const [dashboard, setDashboard] = React.useState(emptyDashboard);
  const [loading, setLoading] = React.useState(true);
  const [databaseReady, setDatabaseReady] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [showVehicleForm, setShowVehicleForm] = React.useState(false);
  const [vehicleQuery, setVehicleQuery] = React.useState("");
  const [vehicleStatus, setVehicleStatus] = React.useState("all");
  const [requestForm, setRequestForm] = React.useState(defaultRequest);
  const [vehicleForm, setVehicleForm] = React.useState(defaultVehicle);
  const [approvalDocument, setApprovalDocument] = React.useState(null);
  const [approvalPreview, setApprovalPreview] = React.useState(null);
  const approvalDocumentInputRef = React.useRef(null);
  const [requestAssignments, setRequestAssignments] = React.useState({});
  const [expandedRequestId, setExpandedRequestId] = React.useState(null);
  const [showRequestForm, setShowRequestForm] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      if (manager) {
        const staffDashboard = await transportService.getStaffDashboard();
        const availableDrivers = await transportService.getAvailableDrivers();
        setDashboard(
          normalizeDashboard({
            ...staffDashboard,
            drivers: availableDrivers || [],
          }),
        );
      } else {
        const requests = await transportService.getMyRequests();
        setDashboard((current) => ({ ...current, requests: requests || [] }));
      }
      setDatabaseReady(true);
    } catch (error) {
      setDatabaseReady(false);
      notify(
        error?.message ||
          "Transport data could not be loaded. Apply the transport fleet, request and assignment migrations first.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [manager, notify]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (!manager) return undefined;
    const releaseTimer = window.setInterval(async () => {
      try {
        const released = await transportService.releaseExpiredAssignments();
        if (Number(released || 0) > 0) {
          await load();
        }
      } catch {
        // The normal dashboard load surfaces connection and permission errors.
        // Keep this quiet background check from interrupting the workspace.
      }
    }, 60_000);
    return () => window.clearInterval(releaseTimer);
  }, [load, manager]);

  const metrics = dashboard.metrics || {};
  const activeVehicles =
    metrics.active_vehicles ??
    dashboard.vehicles.filter((vehicle) => vehicle.status !== "OUT_OF_SERVICE")
      .length;
  const onTrip =
    metrics.on_trip ??
    dashboard.vehicles.filter((vehicle) => vehicle.status === "ON_TRIP").length;
  const dueMaintenance =
    metrics.maintenance_due ??
    dashboard.maintenance.filter((item) =>
      ["DUE", "OVERDUE"].includes(item.status),
    ).length;
  const openRequests =
    metrics.open_requests ??
    dashboard.requests.filter((request) =>
      ["OPEN", "REVIEW", "APPROVED", "SCHEDULED"].includes(request.status),
    ).length;
  const fuelAnomalies =
    metrics.fuel_anomalies ??
    dashboard.fuel_logs.filter((log) => log.anomaly_flag).length;
  const geofenceAlerts =
    metrics.geofence_alerts ??
    dashboard.vehicles.filter(
      (vehicle) => vehicle.geofence_status === "OUTSIDE",
    ).length;

  const submitRequest = async (event) => {
    event.preventDefault();
    const requiresApprovalDocument =
      requestForm.requestType !== "EMERGENCY" || requestForm.outsideCampus;
    if (requiresApprovalDocument && !approvalDocument) {
      notify(
        "Upload an approval document for non-emergency or outside-campus requests.",
        "error",
      );
      return;
    }
    if (
      requestForm.returnDate &&
      requestForm.travelDate &&
      requestForm.returnDate < requestForm.travelDate
    ) {
      notify("Return date cannot be before the travel date.", "error");
      return;
    }
    if (
      requestForm.returnDate === requestForm.travelDate &&
      requestForm.departureTime &&
      requestForm.returnTime &&
      requestForm.returnTime < requestForm.departureTime
    ) {
      notify(
        "Return time cannot be before departure time when using the same day.",
        "error",
      );
      return;
    }
    setBusy("request");
    try {
      await transportService.createRequest({
        ...requestForm,
        approvalDocument,
      });
      notify("Transport request submitted to the transport office.");
      setRequestForm(defaultRequest);
      setApprovalDocument(null);
      setShowRequestForm(false);
      if (approvalDocumentInputRef.current) {
        approvalDocumentInputRef.current.value = "";
      }
      await load();
    } catch (error) {
      notify(
        error?.message || "Transport request could not be submitted",
        "error",
      );
    } finally {
      setBusy("");
    }
  };

  const handleApprovalDocumentChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setApprovalDocument(null);
      return;
    }
    if (
      file.size <= 0 ||
      file.size > MAX_APPROVAL_DOCUMENT_BYTES ||
      !APPROVAL_DOCUMENT_TYPES.includes(file.type)
    ) {
      notify(
        "Approval documents must be a PDF, JPG or PNG up to 5 MB.",
        "error",
      );
      event.target.value = "";
      setApprovalDocument(null);
      return;
    }
    setApprovalDocument(file);
  };

  const viewApprovalDocument = async (request) => {
    if (!request.approval_document_path) {
      notify("The approval document is not available for viewing.", "error");
      return;
    }
    setApprovalPreview({
      requestId: request.id,
      name: request.approval_document_name || "Approval document",
      mimeType: request.approval_document_mime_type || "application/pdf",
      url: "",
      loading: true,
    });
    try {
      const url = await transportService.getApprovalDocumentUrl(
        request.approval_document_path,
      );
      setApprovalPreview((current) =>
        current?.requestId === request.id
          ? { ...current, url, loading: false }
          : current,
      );
    } catch (error) {
      setApprovalPreview(null);
      notify(
        error?.message || "Approval document could not be opened",
        "error",
      );
    }
  };

  const updateRequest = async (request, status) => {
    setBusy(`request-${request.id}`);
    try {
      await transportService.updateRequestStatus(request.id, status);
      notify(
        `Request ${request.request_number || ""} marked ${titleCase(status)}.`,
      );
      await load();
    } catch (error) {
      notify(error?.message || "Request status could not be updated", "error");
    } finally {
      setBusy("");
    }
  };

  const setRequestAssignment = (requestId, field, value) => {
    setRequestAssignments((current) => ({
      ...current,
      [requestId]: {
        ...(current[requestId] || {}),
        [field]: value,
      },
    }));
  };

  const vehiclesForRequest = (request) =>
    dashboard.vehicles.filter((vehicle) => {
      const requestedType = String(request.vehicle_type || "any").toLowerCase();
      const vehicleType = String(vehicle.vehicle_type || "").toLowerCase();
      return (
        vehicle.status === "AVAILABLE" &&
        Number(vehicle.capacity || 0) >= Number(request.passengers || 1) &&
        (requestedType === "any" || vehicleType === requestedType) &&
        !tripOverlapsRequest(request, { vehicleId: vehicle.id })
      );
    });

  const requestTimeWindow = (request) => {
    if (!request.travel_date || !request.departure_time) return null;
    const departureAt = new Date(
      `${String(request.travel_date).slice(0, 10)}T${String(
        request.departure_time,
      ).slice(0, 5)}`,
    );
    if (Number.isNaN(departureAt.getTime())) return null;
    const sameDayMidnightPlaceholder =
      request.return_time &&
      String(request.return_date || request.travel_date).slice(0, 10) ===
        String(request.travel_date).slice(0, 10) &&
      timeLabel(request.return_time) === "00:00";
    const hasReturnTime = request.return_time && !sameDayMidnightPlaceholder;
    const returnAt = hasReturnTime
      ? new Date(
          `${String(request.return_date || request.travel_date).slice(0, 10)}T${String(
            request.return_time,
          ).slice(0, 5)}`,
        )
      : request.return_date
        ? new Date(`${String(request.return_date).slice(0, 10)}T23:59:59`)
        : new Date(departureAt.getTime() + 60 * 60 * 1000);
    return {
      departureAt,
      returnAt: Number.isNaN(returnAt.getTime())
        ? new Date(departureAt.getTime() + 60 * 60 * 1000)
        : returnAt,
    };
  };

  const tripOverlapsRequest = (request, { vehicleId, driverId }) => {
    const requestedWindow = requestTimeWindow(request);
    if (!requestedWindow) return false;
    return dashboard.trips.some((trip) => {
      if (vehicleId && trip.vehicle_id !== vehicleId) return false;
      if (driverId && trip.driver_id !== driverId) return false;
      if (!trip.departure_at) return false;
      const tripDeparture = new Date(trip.departure_at);
      const tripRequest = dashboard.requests.find(
        (request) => request.trip_id === trip.id,
      );
      const tripReturn = trip.return_at
        ? new Date(trip.return_at)
        : tripRequest?.return_date
          ? new Date(`${String(tripRequest.return_date).slice(0, 10)}T23:59:59`)
          : new Date(tripDeparture.getTime() + 60 * 60 * 1000);
      return (
        tripDeparture < requestedWindow.returnAt &&
        tripReturn > requestedWindow.departureAt
      );
    });
  };

  const driversForRequest = (request) =>
    dashboard.drivers.filter(
      (driver) =>
        !tripOverlapsRequest(request, {
          driverId: driver.driver_id,
        }),
    );

  const assignRequest = async (request) => {
    const assignment = requestAssignments[request.id] || {};
    if (!assignment.vehicleId || !assignment.driverProfileId) {
      notify(
        "Select an available vehicle and a Driver before scheduling.",
        "error",
      );
      return;
    }
    setBusy(`assign-${request.id}`);
    try {
      const trip = await transportService.assignRequest({
        requestId: request.id,
        vehicleId: assignment.vehicleId,
        driverProfileId: assignment.driverProfileId,
      });
      notify(
        `${request.request_number || "Request"} scheduled with ${
          trip?.fleet_number || "the selected vehicle"
        } and ${trip?.driver_name || "the selected driver"}.`,
      );
      setRequestAssignments((current) => {
        const next = { ...current };
        delete next[request.id];
        return next;
      });
      await load();
    } catch (error) {
      notify(error?.message || "Request could not be scheduled", "error");
    } finally {
      setBusy("");
    }
  };

  const submitVehicle = async (event) => {
    event.preventDefault();
    setBusy("vehicle");
    try {
      const createdVehicle = await transportService.createVehicle(vehicleForm);
      notify(
        `${createdVehicle?.fleet_number || "Vehicle"} added to the fleet.`,
      );
      setVehicleForm(defaultVehicle);
      setShowVehicleForm(false);
      await load();
    } catch (error) {
      notify(error?.message || "Vehicle could not be added", "error");
    } finally {
      setBusy("");
    }
  };

  const updateVehicleStatus = async (vehicle, status) => {
    setBusy(`vehicle-${vehicle.id}`);
    try {
      await transportService.updateVehicleStatus(vehicle.id, status);
      notify(`${vehicle.fleet_number} is now ${titleCase(status)}.`);
      await load();
    } catch (error) {
      notify(error?.message || "Vehicle status could not be updated", "error");
    } finally {
      setBusy("");
    }
  };

  const downloadReport = () => {
    const rows = dashboard.vehicles.map((vehicle) =>
      [
        vehicle.fleet_number,
        vehicle.registration_number,
        vehicle.make_model,
        vehicle.vehicle_type,
        vehicle.powertrain,
        vehicle.status,
        vehicle.capacity,
        vehicle.odometer_km,
        vehicle.health_score,
        vehicle.geofence_status,
      ]
        .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
        .join(","),
    );
    const csv = [
      "Fleet number,Registration,Make/model,Type,Powertrain,Status,Capacity,Odometer km,Health score,Geofence",
      ...rows,
    ].join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `transport-fleet-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    notify("Fleet report downloaded.");
  };

  const filteredVehicles = dashboard.vehicles.filter((vehicle) => {
    const haystack =
      `${vehicle.fleet_number || ""} ${vehicle.registration_number || ""} ${vehicle.make_model || ""}`.toLowerCase();
    return (
      (!vehicleQuery || haystack.includes(vehicleQuery.toLowerCase())) &&
      (vehicleStatus === "all" || vehicle.status === vehicleStatus)
    );
  });

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
        <Metric
          label="Fleet"
          value={metrics.total_vehicles ?? dashboard.vehicles.length}
          detail={`${activeVehicles} active`}
          icon={Truck}
        />
        <Metric
          label="On trip"
          value={onTrip}
          detail="Live dispatch status"
          icon={Navigation}
          tone="text-indigo-700"
        />
        <Metric
          label="Open requests"
          value={openRequests}
          detail="Awaiting transport action"
          icon={ClipboardList}
          tone="text-amber-700"
        />
        <Metric
          label="Service due"
          value={dueMaintenance}
          detail="Next 30 days or overdue"
          icon={Wrench}
          tone="text-orange-700"
        />
        <Metric
          label="Fuel alerts"
          value={fuelAnomalies}
          detail="Anomalies in last 30 days"
          icon={Fuel}
          tone="text-rose-700"
        />
        <Metric
          label="Geofence alerts"
          value={geofenceAlerts}
          detail="Vehicles outside zones"
          icon={LocateFixed}
          tone="text-violet-700"
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.35fr_1fr]">
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <SectionTitle
            icon={Radio}
            title="Live fleet visibility"
            description="GPS position, geofence state and vehicle health in one view."
            action={
              <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">
                <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Telematics connected
              </Badge>
            }
          />
          <div className="mt-5 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <div className="relative min-h-64 overflow-hidden rounded-xl border border-blue-100 bg-[radial-gradient(circle_at_25%_30%,rgba(59,130,246,.18),transparent_28%),linear-gradient(135deg,#eff6ff,#f8fafc)]">
              <div className="absolute inset-0 opacity-50 [background-image:linear-gradient(rgba(148,163,184,.18)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,.18)_1px,transparent_1px)] [background-size:28px_28px]" />
              <div
                className="absolute left-[22%] top-[30%] h-3 w-3 rounded-full bg-blue-600 ring-4 ring-blue-200"
                title="Live vehicle"
              />
              <div
                className="absolute left-[65%] top-[48%] h-3 w-3 rounded-full bg-emerald-600 ring-4 ring-emerald-200"
                title="Live vehicle"
              />
              <div
                className="absolute left-[48%] top-[72%] h-3 w-3 rounded-full bg-amber-500 ring-4 ring-amber-200"
                title="Vehicle alert"
              />
              <div className="absolute bottom-3 left-3 rounded-lg border bg-white/90 px-3 py-2 text-xs shadow-sm backdrop-blur">
                <p className="font-semibold text-slate-800">
                  Operational geofence
                </p>
                <p className="mt-0.5 text-slate-500">Campus · 3 active zones</p>
              </div>
              <MapPin className="absolute right-[29%] top-[42%] h-6 w-6 text-slate-400" />
            </div>
            <div className="space-y-2">
              {dashboard.vehicles.slice(0, 5).map((vehicle) => (
                <div key={vehicle.id} className="rounded-lg border p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-blue-700" />
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          {vehicle.fleet_number || "Unnumbered vehicle"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {vehicle.make_model ||
                            vehicle.registration_number ||
                            "Vehicle"}
                        </p>
                      </div>
                    </div>
                    <Badge className={statusClass(vehicle.status)}>
                      {titleCase(vehicle.status)}
                    </Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                    <span>{vehicle.driver_name || "Driver unassigned"}</span>
                    <span>
                      {vehicle.geofence_status === "OUTSIDE"
                        ? "Outside geofence"
                        : "Inside geofence"}
                    </span>
                    <span>Health {Number(vehicle.health_score ?? 0)}%</span>
                  </div>
                </div>
              ))}
              {!dashboard.vehicles.length && (
                <EmptyState title="No vehicles connected yet">
                  Add a vehicle to start tracking fleet health and location.
                </EmptyState>
              )}
            </div>
          </div>
        </section>

        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <SectionTitle
            icon={CalendarDays}
            title="Dispatch pulse"
            description="Upcoming trips and service demand."
            action={
              <button
                type="button"
                onClick={() => onViewChange?.("dispatch")}
                className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700"
              >
                Open dispatch <ChevronRight className="h-3.5 w-3.5" />
              </button>
            }
          />
          <div className="mt-4 space-y-3">
            {dashboard.trips.slice(0, 4).map((trip) => (
              <div
                key={trip.id}
                className="flex items-start gap-3 rounded-lg border p-3"
              >
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-indigo-50 text-indigo-700">
                  <Route className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {trip.origin} → {trip.destination}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {dateTimeLabel(trip.departure_at)} ·{" "}
                    {trip.fleet_number || "Vehicle pending"}
                  </p>
                </div>
                <Badge className={statusClass(trip.status)}>
                  {titleCase(trip.status)}
                </Badge>
              </div>
            ))}
            {!dashboard.trips.length && (
              <EmptyState icon={Route} title="No trips scheduled">
                Approved requests will appear here when dispatch assigns a
                vehicle.
              </EmptyState>
            )}
          </div>
        </section>
      </div>

      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <SectionTitle
          icon={Sparkles}
          title="Connected operations"
          description="The Transport workspace is ready for live integrations as devices and policies are connected."
        />
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <CapabilityCard
            icon={LocateFixed}
            title="GPS & geofencing"
            detail="Live coordinates, zone entry/exit alerts and route deviation monitoring."
          />
          <CapabilityCard
            icon={Activity}
            title="Vehicle health"
            detail="Service intervals, odometer rules, battery state and repair history."
            tone="emerald"
          />
          <CapabilityCard
            icon={ShieldCheck}
            title="Driver safety"
            detail="Harsh braking, speeding, distraction events and coaching scorecards."
            tone="amber"
          />
          <CapabilityCard
            icon={CloudCog}
            title="Open integrations"
            detail="ERP, HR, fuel-card, IoT and reporting APIs with auditable documents."
            tone="violet"
          />
        </div>
      </section>
    </div>
  );

  const renderFleet = () => (
    <div className="space-y-6">
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <SectionTitle
          icon={Truck}
          title="Fleet inventory"
          description="Manage vehicles, capacity, powertrain, live health and statutory dates."
          action={
            manager && (
              <button
                type="button"
                onClick={() => setShowVehicleForm((value) => !value)}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground"
              >
                {showVehicleForm ? (
                  <X className="h-4 w-4" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {showVehicleForm ? "Close" : "Add vehicle"}
              </button>
            )
          }
        />
        {showVehicleForm && (
          <form
            onSubmit={submitVehicle}
            className="mt-5 grid gap-3 rounded-xl border border-blue-100 bg-blue-50/40 p-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            <div className="rounded-lg border border-blue-100 bg-white px-3 py-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Fleet number (automatic)
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-2">
                <span className="font-mono text-sm font-semibold text-primary">
                  {fleetPrefix(vehicleForm.vehicleType)}-###
                </span>
                <span className="text-[11px] font-normal normal-case tracking-normal text-slate-500">
                  Assigned on save
                </span>
              </div>
            </div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Registration
              <input
                required
                value={vehicleForm.registrationNumber}
                onChange={(e) =>
                  setVehicleForm({
                    ...vehicleForm,
                    registrationNumber: e.target.value,
                  })
                }
                placeholder="KDA 123A"
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm normal-case tracking-normal"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Make / model
              <input
                required
                value={vehicleForm.makeModel}
                onChange={(e) =>
                  setVehicleForm({ ...vehicleForm, makeModel: e.target.value })
                }
                placeholder="Toyota Coaster"
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm normal-case tracking-normal"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Vehicle type
              <select
                value={vehicleForm.vehicleType}
                onChange={(e) =>
                  setVehicleForm({
                    ...vehicleForm,
                    vehicleType: e.target.value,
                  })
                }
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm normal-case tracking-normal"
              >
                <option value="bus">Bus</option>
                <option value="van">Van / minibus</option>
                <option value="pickup">Pickup / utility</option>
                <option value="saloon">Saloon / executive</option>
              </select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Powertrain
              <select
                value={vehicleForm.powertrain}
                onChange={(e) =>
                  setVehicleForm({ ...vehicleForm, powertrain: e.target.value })
                }
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm normal-case tracking-normal"
              >
                <option value="ICE">ICE</option>
                <option value="HYBRID">Hybrid</option>
                <option value="EV">Electric</option>
              </select>
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Passenger capacity
              <input
                type="number"
                min="1"
                required
                value={vehicleForm.capacity}
                onChange={(e) =>
                  setVehicleForm({ ...vehicleForm, capacity: e.target.value })
                }
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm normal-case tracking-normal"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Odometer km
              <input
                type="number"
                min="0"
                value={vehicleForm.odometerKm}
                onChange={(e) =>
                  setVehicleForm({ ...vehicleForm, odometerKm: e.target.value })
                }
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm normal-case tracking-normal"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Next service
              <input
                type="date"
                value={vehicleForm.nextServiceDate}
                onChange={(e) =>
                  setVehicleForm({
                    ...vehicleForm,
                    nextServiceDate: e.target.value,
                  })
                }
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm normal-case tracking-normal"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Insurance expiry
              <input
                type="date"
                value={vehicleForm.insuranceExpiry}
                onChange={(e) =>
                  setVehicleForm({
                    ...vehicleForm,
                    insuranceExpiry: e.target.value,
                  })
                }
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm normal-case tracking-normal"
              />
            </label>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Inspection expiry
              <input
                type="date"
                value={vehicleForm.inspectionExpiry}
                onChange={(e) =>
                  setVehicleForm({
                    ...vehicleForm,
                    inspectionExpiry: e.target.value,
                  })
                }
                className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm normal-case tracking-normal"
              />
            </label>
            <div className="flex items-end sm:col-span-2 lg:col-span-4">
              <button
                disabled={busy === "vehicle"}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                {busy === "vehicle" ? "Saving…" : "Save vehicle"}
              </button>
            </div>
          </form>
        )}
        <div className="mt-5 flex flex-wrap gap-3 rounded-xl border bg-slate-50 p-3">
          <label className="flex min-w-56 flex-1 items-center gap-2 rounded-lg border bg-white px-3">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={vehicleQuery}
              onChange={(e) => setVehicleQuery(e.target.value)}
              placeholder="Search fleet number, registration or model"
              className="min-w-0 flex-1 py-2 text-sm outline-none"
            />
          </label>
          <select
            value={vehicleStatus}
            onChange={(e) => setVehicleStatus(e.target.value)}
            className="rounded-lg border bg-white px-3 py-2 text-sm"
          >
            <option value="all">All statuses</option>
            <option value="AVAILABLE">Available</option>
            <option value="ON_TRIP">On trip</option>
            <option value="MAINTENANCE">Maintenance</option>
            <option value="OUT_OF_SERVICE">Out of service</option>
          </select>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm font-semibold text-slate-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
        <div className="mt-4 overflow-x-auto rounded-xl border">
          <table className="min-w-[1000px] w-full text-left text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Vehicle</th>
                <th className="px-4 py-3">Powertrain</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Health / location</th>
                <th className="px-4 py-3">Compliance</th>
                <th className="px-4 py-3 text-right">Update</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredVehicles.map((vehicle) => (
                <tr key={vehicle.id} className="align-top">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-900">
                      {vehicle.fleet_number}
                    </p>
                    <p className="text-xs text-slate-500">
                      {vehicle.registration_number} · {vehicle.make_model}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Capacity {vehicle.capacity} ·{" "}
                      {vehicle.driver_name || "Driver unassigned"}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className="border-slate-200 bg-slate-50 text-slate-700">
                      {vehicle.powertrain === "EV"
                        ? "Electric"
                        : vehicle.powertrain === "HYBRID"
                          ? "Hybrid"
                          : "ICE"}
                    </Badge>
                    <p className="mt-1 text-xs text-slate-500">
                      {titleCase(vehicle.vehicle_type)}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={statusClass(vehicle.status)}>
                      {titleCase(vehicle.status)}
                    </Badge>
                    <p className="mt-1 text-xs text-slate-500">
                      {vehicle.geofence_status === "OUTSIDE"
                        ? "Outside geofence"
                        : "Inside geofence"}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold">
                      {Number(vehicle.health_score ?? 0)}% health
                    </p>
                    <p className="text-xs text-slate-500">
                      {vehicle.powertrain === "EV"
                        ? `Battery ${vehicle.battery_level_percent ?? "—"}%`
                        : `Fuel ${vehicle.fuel_level_percent ?? "—"}%`}
                    </p>
                    <p className="text-xs text-slate-500">
                      GPS{" "}
                      {vehicle.gps_updated_at
                        ? dateTimeLabel(vehicle.gps_updated_at)
                        : "not connected"}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    <p>Service {dateLabel(vehicle.next_service_date)}</p>
                    <p>Insurance {dateLabel(vehicle.insurance_expiry)}</p>
                    <p>Inspection {dateLabel(vehicle.inspection_expiry)}</p>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {manager ? (
                      <select
                        disabled={busy === `vehicle-${vehicle.id}`}
                        value={vehicle.status}
                        onChange={(e) =>
                          updateVehicleStatus(vehicle, e.target.value)
                        }
                        className="rounded-lg border bg-white px-2 py-1.5 text-xs"
                      >
                        <option value="AVAILABLE">Available</option>
                        <option value="ON_TRIP">On trip</option>
                        <option value="MAINTENANCE">Maintenance</option>
                        <option value="OUT_OF_SERVICE">Out of service</option>
                      </select>
                    ) : (
                      <span className="text-xs text-slate-400">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filteredVehicles.length && (
            <div className="p-8">
              <EmptyState title="No vehicles found">
                Add a vehicle or clear the filters.
              </EmptyState>
            </div>
          )}
        </div>
      </section>
    </div>
  );

  const renderDispatch = () => (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Metric
          label="Requests in queue"
          value={openRequests}
          detail="Review and approve"
          icon={ClipboardList}
          tone="text-amber-700"
        />
        <Metric
          label="Scheduled trips"
          value={
            dashboard.trips.filter((trip) =>
              ["SCHEDULED", "DISPATCHED"].includes(trip.status),
            ).length
          }
          detail="Vehicle assignment"
          icon={CalendarDays}
          tone="text-indigo-700"
        />
        <Metric
          label="Geofence state"
          value={
            geofenceAlerts
              ? `${geofenceAlerts} alert${geofenceAlerts > 1 ? "s" : ""}`
              : "Clear"
          }
          detail="Live route monitoring"
          icon={LocateFixed}
          tone={geofenceAlerts ? "text-rose-700" : "text-emerald-700"}
        />
      </div>
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <SectionTitle
          icon={Route}
          title="Route planning & dispatch"
          description="Approve requests, assign a suitable vehicle and keep multi-stop routes visible."
          action={
            <Badge className="border-blue-200 bg-blue-50 text-blue-700">
              <Sparkles className="mr-1 h-3.5 w-3.5" />
              Optimization ready
            </Badge>
          }
        />
        <div className="mt-5 overflow-x-auto rounded-xl border">
          <table className="min-w-[1000px] w-full text-left text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Trip / route</th>
                <th className="px-4 py-3">Departure</th>
                <th className="px-4 py-3">Vehicle / driver</th>
                <th className="px-4 py-3">Passengers</th>
                <th className="px-4 py-3">Tracking</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {dashboard.trips.map((trip) => (
                <tr key={trip.id}>
                  <td className="px-4 py-3">
                    <p className="font-semibold">
                      {trip.trip_number || "Trip"}
                    </p>
                    <p className="text-xs text-slate-500">
                      {trip.origin} → {trip.destination}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {dateTimeLabel(trip.departure_at)}
                    {trip.return_at && (
                      <>
                        <br />
                        Return {dateTimeLabel(trip.return_at)}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {trip.fleet_number || "Unassigned"}
                    <br />
                    {trip.driver_name || "Driver unassigned"}
                  </td>
                  <td className="px-4 py-3">{trip.passengers || "—"}</td>
                  <td className="px-4 py-3">
                    <Badge
                      className={
                        trip.geofence_status === "DEVIATED"
                          ? "border-rose-200 bg-rose-50 text-rose-700"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700"
                      }
                    >
                      {titleCase(trip.geofence_status || "NOT_STARTED")}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={statusClass(trip.status)}>
                      {titleCase(trip.status)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!dashboard.trips.length && (
            <div className="p-8">
              <EmptyState icon={Route} title="No trips in dispatch">
                Approved transport requests become trips after assignment.
              </EmptyState>
            </div>
          )}
        </div>
      </section>
    </div>
  );

  const renderMaintenance = () => (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Metric
          label="Due / overdue"
          value={dueMaintenance}
          detail="Service attention"
          icon={Wrench}
          tone="text-amber-700"
        />
        <Metric
          label="Fleet health"
          value={
            dashboard.vehicles.length
              ? `${Math.round(dashboard.vehicles.reduce((sum, item) => sum + Number(item.health_score || 0), 0) / dashboard.vehicles.length)}%`
              : "—"
          }
          detail="Average health score"
          icon={Gauge}
          tone="text-emerald-700"
        />
        <Metric
          label="Repair history"
          value={dashboard.maintenance.length}
          detail="Tracked work orders"
          icon={FileText}
          tone="text-blue-700"
        />
        <Metric
          label="Sensors"
          value={
            new Set(dashboard.sensor_readings.map((item) => item.vehicle_id))
              .size
          }
          detail="Vehicles reporting"
          icon={Activity}
          tone="text-violet-700"
        />
      </div>
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <SectionTitle
          icon={Wrench}
          title="Predictive maintenance"
          description="Service reminders combine dates, odometer thresholds and health telemetry."
        />
        <div className="mt-5 grid gap-3 lg:grid-cols-2">
          {dashboard.maintenance.map((item) => (
            <div key={item.id} className="rounded-lg border p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">
                    {item.fleet_number || "Vehicle"} · {item.maintenance_type}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.make_model || ""}
                  </p>
                </div>
                <Badge className={statusClass(item.status)}>
                  {titleCase(item.status)}
                </Badge>
              </div>
              <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-3">
                <span>Due {dateLabel(item.due_date)}</span>
                <span>
                  Odometer{" "}
                  {item.due_odometer_km ? `${item.due_odometer_km} km` : "—"}
                </span>
                <span>Cost {money(item.cost)}</span>
              </div>
              {item.description && (
                <p className="mt-2 text-sm text-slate-600">
                  {item.description}
                </p>
              )}
            </div>
          ))}
          {!dashboard.maintenance.length && (
            <EmptyState icon={Wrench} title="No maintenance records">
              Service plans and repair history will appear here.
            </EmptyState>
          )}
        </div>
      </section>
    </div>
  );

  const renderSafety = () => (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Metric
          label="Safety events"
          value={metrics.safety_events ?? dashboard.safety_events.length}
          detail="Last 30 days"
          icon={ShieldCheck}
          tone="text-amber-700"
        />
        <Metric
          label="Driver score"
          value={dashboard.vehicles.length ? "—" : "—"}
          detail="Add driver scorecards"
          icon={UserRound}
          tone="text-blue-700"
        />
        <Metric
          label="Video telematics"
          value="Ready"
          detail="Dashcam events supported"
          icon={Radio}
          tone="text-violet-700"
        />
        <Metric
          label="Coaching"
          value={
            dashboard.safety_events.filter(
              (item) => item.coaching_status === "OPEN",
            ).length
          }
          detail="Events needing review"
          icon={CheckCircle2}
          tone="text-rose-700"
        />
      </div>
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <SectionTitle
          icon={ShieldCheck}
          title="Driver safety & video events"
          description="Review harsh braking, acceleration, distraction and near-miss signals before coaching."
        />
        <div className="mt-5 overflow-x-auto rounded-xl border">
          <table className="min-w-[900px] w-full text-left text-sm">
            <thead className="border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Occurred</th>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">Vehicle / driver</th>
                <th className="px-4 py-3">Severity</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Coaching</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {dashboard.safety_events.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {dateTimeLabel(item.occurred_at)}
                  </td>
                  <td className="px-4 py-3 font-semibold">
                    {titleCase(item.event_type)}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {item.fleet_number || "—"}
                    <br />
                    {item.driver_name || "Driver unassigned"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      className={
                        item.severity === "critical" || item.severity === "high"
                          ? "border-rose-200 bg-rose-50 text-rose-700"
                          : "border-amber-200 bg-amber-50 text-amber-700"
                      }
                    >
                      {titleCase(item.severity)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {titleCase(item.source)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={statusClass(item.coaching_status)}>
                      {titleCase(item.coaching_status)}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!dashboard.safety_events.length && (
            <div className="p-8">
              <EmptyState icon={ShieldCheck} title="No safety events">
                Connected dashcams and telematics events will appear here.
              </EmptyState>
            </div>
          )}
        </div>
      </section>
    </div>
  );

  const renderFuel = () => {
    const litres = dashboard.fuel_logs.reduce(
      (sum, item) => sum + Number(item.litres || 0),
      0,
    );
    const spend = dashboard.fuel_logs.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0,
    );
    const evCount = dashboard.vehicles.filter(
      (item) => item.powertrain === "EV" || item.powertrain === "HYBRID",
    ).length;
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          <Metric
            label="Fuel logged"
            value={`${litres.toFixed(1)} L`}
            detail="Recent fuel-card records"
            icon={Fuel}
            tone="text-blue-700"
          />
          <Metric
            label="Fuel spend"
            value={money(spend)}
            detail="Recorded transactions"
            icon={BarChart3}
            tone="text-amber-700"
          />
          <Metric
            label="Anomalies"
            value={fuelAnomalies}
            detail="Drops or idle spikes"
            icon={AlertTriangle}
            tone="text-rose-700"
          />
          <Metric
            label="EV / hybrid"
            value={evCount}
            detail="Sustainable fleet"
            icon={BatteryCharging}
            tone="text-emerald-700"
          />
        </div>
        <div className="grid gap-6 xl:grid-cols-[1.3fr_1fr]">
          <section className="rounded-xl border bg-white p-5 shadow-sm">
            <SectionTitle
              icon={Fuel}
              title="Fuel management"
              description="Fuel-card usage, idle-time signals and theft anomaly detection."
            />
            <div className="mt-5 overflow-x-auto rounded-xl border">
              <table className="min-w-[750px] w-full text-left text-sm">
                <thead className="border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Logged</th>
                    <th className="px-4 py-3">Vehicle</th>
                    <th className="px-4 py-3">Litres</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Source</th>
                    <th className="px-4 py-3">Signal</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {dashboard.fuel_logs.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {dateTimeLabel(item.logged_at)}
                      </td>
                      <td className="px-4 py-3 font-semibold">
                        {item.fleet_number || item.vehicle_id || "—"}
                      </td>
                      <td className="px-4 py-3">{item.litres} L</td>
                      <td className="px-4 py-3">{money(item.amount)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {titleCase(item.fuel_source)}
                      </td>
                      <td className="px-4 py-3">
                        {item.anomaly_flag ? (
                          <Badge className="border-rose-200 bg-rose-50 text-rose-700">
                            Review anomaly
                          </Badge>
                        ) : (
                          <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">
                            Normal
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!dashboard.fuel_logs.length && (
                <div className="p-8">
                  <EmptyState icon={Fuel} title="No fuel logs">
                    Fuel-card and manual fuel entries will be reconciled here.
                  </EmptyState>
                </div>
              )}
            </div>
          </section>
          <section className="rounded-xl border bg-white p-5 shadow-sm">
            <SectionTitle
              icon={ShieldCheck}
              title="Compliance centre"
              description="Expiry dates, HOS/ELD logs and inspection records."
            />
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <FileText className="h-4 w-4 text-blue-700" />
                  Insurance & inspection
                </span>
                <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">
                  Tracked
                </Badge>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <CalendarDays className="h-4 w-4 text-indigo-700" />
                  Driver licences
                </span>
                <Badge className="border-amber-200 bg-amber-50 text-amber-700">
                  Expiry alerts
                </Badge>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <ClockIcon className="h-4 w-4 text-violet-700" />
                  HOS / ELD logs
                </span>
                <Badge className="border-blue-200 bg-blue-50 text-blue-700">
                  API ready
                </Badge>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <BatteryCharging className="h-4 w-4 text-emerald-700" />
                  Carbon & charging
                </span>
                <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">
                  EV ready
                </Badge>
              </div>
            </div>
          </section>
        </div>
      </div>
    );
  };

  const renderRequests = () => {
    if (manager) {
      return (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-4">
            <Metric
              label="All open"
              value={openRequests}
              detail="Needs transport action"
              icon={ClipboardList}
              tone="text-amber-700"
            />
            <Metric
              label="Urgent"
              value={
                dashboard.requests.filter((item) => item.priority === "urgent")
                  .length
              }
              detail="Escalate first"
              icon={AlertTriangle}
              tone="text-rose-700"
            />
            <Metric
              label="Approved"
              value={
                dashboard.requests.filter((item) => item.status === "APPROVED")
                  .length
              }
              detail="Ready to schedule"
              icon={CheckCircle2}
              tone="text-emerald-700"
            />
            <Metric
              label="Today"
              value={
                dashboard.requests.filter(
                  (item) =>
                    String(item.travel_date || "").slice(0, 10) ===
                    new Date().toISOString().slice(0, 10),
                ).length
              }
              detail="Travel dates"
              icon={CalendarDays}
              tone="text-indigo-700"
            />
          </div>
          <section className="rounded-xl border bg-white p-5 shadow-sm">
            <SectionTitle
              icon={ClipboardList}
              title="Transport request queue"
              description="Requests from teaching and support staff, ready for review and dispatch."
              action={
                <button
                  type="button"
                  onClick={load}
                  className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </button>
              }
            />
            <div className="mt-5 overflow-x-auto rounded-xl border">
              <div className="min-w-[980px]">
                <div className="grid grid-cols-[2fr_1.4fr_2fr_1.4fr_1.2fr_1fr_0.8fr] gap-3 border-b bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <span>Request</span>
                  <span>Requester</span>
                  <span>Route</span>
                  <span>Travel / return</span>
                  <span>Vehicle</span>
                  <span>Status</span>
                  <span className="text-right">Action</span>
                </div>
                {dashboard.requests.map((request) => (
                  <div key={request.id} className="border-b last:border-b-0">
                    <div className="grid grid-cols-[2fr_1.4fr_2fr_1.4fr_1.2fr_1fr_0.8fr] items-center gap-3 px-4 py-3 text-sm">
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-900">
                          {request.purpose}
                        </p>
                        <p className="mt-1 truncate text-xs text-slate-500">
                          {request.request_number}
                        </p>
                      </div>
                      <p className="truncate text-xs text-slate-600">
                        {request.requester_name || "Staff requester"}
                      </p>
                      <p className="truncate text-xs text-slate-600">
                        {request.origin} → {request.destination}
                      </p>
                      <div className="text-xs text-slate-600">
                        <p>{dateLabel(request.travel_date)}</p>
                        <p className="mt-1">
                          → {dateLabel(request.return_date)}
                        </p>
                      </div>
                      <div className="text-xs text-slate-600">
                        <p>{titleCase(request.vehicle_type)}</p>
                        <p className="mt-1">
                          {request.passengers} passenger
                          {request.passengers === 1 ? "" : "s"}
                        </p>
                      </div>
                      <Badge className={statusClass(request.status)}>
                        {titleCase(request.status)}
                      </Badge>
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedRequestId((current) =>
                            current === request.id ? null : request.id,
                          )
                        }
                        className="justify-self-end rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                      >
                        {expandedRequestId === request.id ? "Hide" : "View"}
                      </button>
                    </div>
                    {expandedRequestId === request.id && (
                      <div className="border-t bg-slate-50/70 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="font-semibold text-slate-900">
                              {request.purpose}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {request.request_number} ·{" "}
                              {request.requester_name || "Staff requester"}
                            </p>
                          </div>
                          <Badge className={statusClass(request.status)}>
                            {titleCase(request.status)}
                          </Badge>
                        </div>
                        <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
                          <span>
                            <strong>Route:</strong> {request.origin} →{" "}
                            {request.destination}
                          </span>
                          <span>
                            <strong>Travel:</strong>{" "}
                            {dateLabel(request.travel_date)}
                          </span>
                          <span>
                            <strong>Departure:</strong>{" "}
                            {timeLabel(request.departure_time)}
                          </span>
                          <span>
                            <strong>Return:</strong>{" "}
                            {dateLabel(request.return_date)}
                          </span>
                          <span>
                            <strong>Return time:</strong>{" "}
                            {returnTimeLabel(request)}
                          </span>
                          <span>
                            <strong>Passengers:</strong> {request.passengers}
                          </span>
                          <span>
                            <strong>Vehicle:</strong>{" "}
                            {titleCase(request.vehicle_type)}
                          </span>
                        </div>
                        {request.notes && (
                          <p className="mt-3 rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
                            {request.notes}
                          </p>
                        )}
                        {request.approval_document_name && (
                          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                            <p className="font-medium text-indigo-700">
                              Approval attached:{" "}
                              {request.approval_document_name}
                            </p>
                            {request.approval_document_path && (
                              <button
                                type="button"
                                onClick={() => viewApprovalDocument(request)}
                                className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-2 py-1 font-semibold text-indigo-700 hover:bg-indigo-50"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                View
                              </button>
                            )}
                          </div>
                        )}
                        {request.trip_id && (
                          <p className="mt-3 rounded-lg bg-indigo-50 p-2 text-xs text-indigo-800">
                            <strong>Scheduled:</strong>{" "}
                            {(() => {
                              const trip = dashboard.trips.find(
                                (item) => item.id === request.trip_id,
                              );
                              return trip
                                ? `${trip.fleet_number || "Vehicle"} · ${
                                    trip.driver_name || "Driver"
                                  }`
                                : "Assignment saved";
                            })()}
                          </p>
                        )}
                        {["OPEN", "REVIEW", "APPROVED"].includes(
                          request.status,
                        ) && (
                          <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-800">
                              Approve and assign dispatch
                            </p>
                            <p className="mt-1 text-xs text-indigo-700">
                              Select an available vehicle and Driver. Date/time
                              conflicts are checked before scheduling.
                            </p>
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                              <select
                                aria-label={`Vehicle for ${request.request_number || request.purpose}`}
                                value={
                                  requestAssignments[request.id]?.vehicleId ||
                                  ""
                                }
                                onChange={(event) =>
                                  setRequestAssignment(
                                    request.id,
                                    "vehicleId",
                                    event.target.value,
                                  )
                                }
                                className="rounded-lg border bg-white px-3 py-2 text-xs text-slate-700"
                              >
                                <option value="">
                                  Select available vehicle
                                </option>
                                {vehiclesForRequest(request).map((vehicle) => (
                                  <option key={vehicle.id} value={vehicle.id}>
                                    {vehicle.fleet_number} ·{" "}
                                    {vehicle.make_model} · {vehicle.capacity}{" "}
                                    seats
                                  </option>
                                ))}
                              </select>
                              <select
                                aria-label={`Driver for ${request.request_number || request.purpose}`}
                                value={
                                  requestAssignments[request.id]
                                    ?.driverProfileId || ""
                                }
                                onChange={(event) =>
                                  setRequestAssignment(
                                    request.id,
                                    "driverProfileId",
                                    event.target.value,
                                  )
                                }
                                className="rounded-lg border bg-white px-3 py-2 text-xs text-slate-700"
                              >
                                <option value="">
                                  Select available Driver
                                </option>
                                {driversForRequest(request).map((driver) => (
                                  <option
                                    key={driver.profile_id}
                                    value={driver.profile_id}
                                  >
                                    {driver.full_name} · {driver.email || ""}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {(!vehiclesForRequest(request).length ||
                              !driversForRequest(request).length) && (
                              <p className="mt-2 text-xs font-medium text-amber-700">
                                {!vehiclesForRequest(request).length
                                  ? "No available vehicle matches the requested type and passenger capacity."
                                  : "No active users with the Driver role are available for this time."}
                              </p>
                            )}
                            <button
                              type="button"
                              disabled={busy === `assign-${request.id}`}
                              onClick={() => assignRequest(request)}
                              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
                            >
                              {busy === `assign-${request.id}`
                                ? "Scheduling…"
                                : "Approve & schedule"}
                            </button>
                          </div>
                        )}
                        <div className="mt-4 flex flex-wrap gap-2">
                          {request.status === "OPEN" && (
                            <button
                              disabled={busy === `request-${request.id}`}
                              onClick={() => updateRequest(request, "REVIEW")}
                              className="rounded-lg border px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50"
                            >
                              Review
                            </button>
                          )}
                          {["OPEN", "REVIEW", "APPROVED"].includes(
                            request.status,
                          ) && (
                            <button
                              disabled={busy === `request-${request.id}`}
                              onClick={() => updateRequest(request, "REJECTED")}
                              className="rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700 disabled:opacity-50"
                            >
                              Reject
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {!dashboard.requests.length && (
                  <EmptyState
                    icon={ClipboardList}
                    title="No transport requests"
                  >
                    Staff submissions will appear in this queue.
                  </EmptyState>
                )}
              </div>
            </div>
          </section>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        {showRequestForm && (
          <div
            className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto overscroll-contain bg-slate-950/50 p-3 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="transport-request-title"
          >
            <section className="my-2 flex max-h-[calc(100vh-1rem)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl sm:my-4 sm:max-h-[calc(100vh-2rem)]">
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
                <div className="min-w-0">
                  <div className="min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-indigo-700">
                      Transport office
                    </p>
                    <h2
                      id="transport-request-title"
                      className="mt-1 text-lg font-bold text-slate-950"
                    >
                      Request transport
                    </h2>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowRequestForm(false)}
                  aria-label="Close transport request"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-indigo-200 hover:text-indigo-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form
                onSubmit={submitRequest}
                className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-4 sm:p-5"
              >
                <div className="rounded-lg border border-slate-200 p-3 sm:p-4">
                  <div className="mb-3">
                    <p className="text-sm font-bold text-slate-900">
                      Trip details
                    </p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Request type
                      <select
                        required
                        value={requestForm.requestType}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            requestType: e.target.value,
                          })
                        }
                        className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      >
                        {requestTypeOptions.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block text-sm font-medium text-slate-700">
                      Priority
                      <select
                        value={requestForm.priority}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            priority: e.target.value,
                          })
                        }
                        className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      >
                        <option value="normal">Normal</option>
                        <option value="low">Low</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </label>
                  </div>
                  <label className="mt-4 block text-sm font-medium text-slate-700">
                    Purpose
                    <input
                      required
                      minLength="3"
                      value={requestForm.purpose}
                      onChange={(e) =>
                        setRequestForm({
                          ...requestForm,
                          purpose: e.target.value,
                        })
                      }
                      placeholder="e.g. Department visit, field trip or delivery"
                      className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    />
                  </label>
                </div>

                <div className="rounded-lg border border-slate-200 p-3 sm:p-4">
                  <div className="mb-3">
                    <p className="text-sm font-bold text-slate-900">Route</p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Pickup / origin
                      <input
                        required
                        value={requestForm.origin}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            origin: e.target.value,
                          })
                        }
                        placeholder="Main campus"
                        className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      />
                    </label>
                    <label className="block text-sm font-medium text-slate-700">
                      Destination
                      <input
                        required
                        value={requestForm.destination}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            destination: e.target.value,
                          })
                        }
                        placeholder="Destination"
                        className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      />
                    </label>
                  </div>
                  <label className="mt-4 flex items-start gap-3 rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={requestForm.outsideCampus}
                      onChange={(e) =>
                        setRequestForm({
                          ...requestForm,
                          outsideCampus: e.target.checked,
                        })
                      }
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>
                      <span className="block font-semibold text-slate-900">
                        Outside-campus or external service
                      </span>
                      <span className="mt-0.5 block text-xs text-slate-600">
                        This requires an approval document before the request
                        can be submitted.
                      </span>
                    </span>
                  </label>
                </div>

                <div className="rounded-lg border border-slate-200 p-3 sm:p-4">
                  <div className="mb-3">
                    <p className="text-sm font-bold text-slate-900">
                      Schedule and vehicle
                    </p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <label className="block text-sm font-medium text-slate-700">
                      Travel date
                      <input
                        required
                        type="date"
                        value={requestForm.travelDate}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            travelDate: e.target.value,
                          })
                        }
                        className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      />
                    </label>
                    <label className="block text-sm font-medium text-slate-700">
                      Return date
                      <input
                        required
                        min={requestForm.travelDate || undefined}
                        type="date"
                        value={requestForm.returnDate}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            returnDate: e.target.value,
                          })
                        }
                        className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      />
                    </label>
                    <label className="block text-sm font-medium text-slate-700">
                      Passengers
                      <input
                        required
                        min="1"
                        type="number"
                        value={requestForm.passengers}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            passengers: e.target.value,
                          })
                        }
                        className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      />
                    </label>
                  </div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-3">
                    <label className="block text-sm font-medium text-slate-700">
                      Departure time
                      <input
                        required
                        type="time"
                        value={requestForm.departureTime}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            departureTime: e.target.value,
                          })
                        }
                        className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      />
                    </label>
                    <label className="block text-sm font-medium text-slate-700">
                      Return time{" "}
                      <span className="font-normal text-slate-400">
                        (optional)
                      </span>
                      <input
                        type="time"
                        value={requestForm.returnTime}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            returnTime: e.target.value,
                          })
                        }
                        className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      />
                    </label>
                    <label className="block text-sm font-medium text-slate-700">
                      Vehicle requested
                      <select
                        value={requestForm.vehicleType}
                        onChange={(e) =>
                          setRequestForm({
                            ...requestForm,
                            vehicleType: e.target.value,
                          })
                        }
                        className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      >
                        {vehicleTypeOptions.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 p-3 sm:p-4">
                  <div className="mb-3 sm:col-span-2">
                    <div>
                      <p className="text-sm font-bold text-slate-900">
                        Supporting information
                      </p>
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label
                      htmlFor="transport-approval-document"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Approval document
                      {requestForm.requestType !== "EMERGENCY" ||
                      requestForm.outsideCampus
                        ? " (required)"
                        : " (optional)"}
                      <input
                        id="transport-approval-document"
                        ref={approvalDocumentInputRef}
                        required={
                          requestForm.requestType !== "EMERGENCY" ||
                          requestForm.outsideCampus
                        }
                        type="file"
                        accept="application/pdf,image/jpeg,image/png"
                        onChange={handleApprovalDocumentChange}
                        className="mt-1.5 block w-full rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-100 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-indigo-700"
                      />
                      {approvalDocument && (
                        <div className="mt-2 flex items-center justify-between gap-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                          <span className="truncate">
                            {approvalDocument.name} ·{" "}
                            {(approvalDocument.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setApprovalDocument(null);
                              if (approvalDocumentInputRef.current) {
                                approvalDocumentInputRef.current.value = "";
                              }
                            }}
                            className="shrink-0 font-semibold text-rose-700 hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </label>
                    <label className="block text-sm font-medium text-slate-700">
                    Notes{" "}
                    <span className="font-normal text-slate-400">
                      (optional)
                    </span>
                    <textarea
                      rows="2"
                      value={requestForm.notes}
                      onChange={(e) =>
                        setRequestForm({
                          ...requestForm,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Accessibility needs, extra stops or cargo details"
                      className="mt-1.5 w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    />
                    </label>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowRequestForm(false)}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={busy === "request"}
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {busy === "request" ? "Submitting…" : "Submit request"}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}
        <section className="rounded-xl border bg-white p-5 shadow-sm">
          <SectionTitle
            icon={ClipboardList}
            title="My requests"
            description="Track review, approval and dispatch updates."
            leading={
              onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="mt-1 shrink-0 text-sm font-semibold text-blue-700 hover:underline"
                >
                  ← Staff dashboard
                </button>
              ) : null
            }
            action={
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setShowRequestForm(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground"
                >
                  <Plus className="h-4 w-4" />
                  Request transport
                </button>
                <button
                  type="button"
                  onClick={load}
                  className="inline-flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </button>
              </div>
            }
          />
          <div className="mt-5 overflow-x-auto rounded-xl border">
            <div className="min-w-[900px]">
              <div className="grid grid-cols-[2fr_2fr_1.5fr_1.3fr_1fr_0.8fr] gap-3 border-b bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <span>Request</span>
                <span>Route</span>
                <span>Travel / return</span>
                <span>Vehicle</span>
                <span>Status</span>
                <span className="text-right">Action</span>
              </div>
              {dashboard.requests.map((request) => (
                <div key={request.id} className="border-b last:border-b-0">
                  <div className="grid grid-cols-[2fr_2fr_1.5fr_1.3fr_1fr_0.8fr] items-center gap-3 px-4 py-3 text-sm">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-slate-900">
                        {request.purpose}
                      </p>
                      <p className="mt-1 truncate text-xs text-slate-500">
                        {request.request_number}
                      </p>
                    </div>
                    <p className="truncate text-xs text-slate-600">
                      {request.origin} → {request.destination}
                    </p>
                    <div className="text-xs text-slate-600">
                      <p>{dateLabel(request.travel_date)}</p>
                      <p className="mt-1">→ {dateLabel(request.return_date)}</p>
                    </div>
                    <div className="text-xs text-slate-600">
                      <p>{titleCase(request.vehicle_type)}</p>
                      <p className="mt-1">
                        {request.passengers} passenger
                        {request.passengers === 1 ? "" : "s"}
                      </p>
                    </div>
                    <Badge className={statusClass(request.status)}>
                      {titleCase(request.status)}
                    </Badge>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedRequestId((current) =>
                          current === request.id ? null : request.id,
                        )
                      }
                      className="justify-self-end rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                    >
                      {expandedRequestId === request.id ? "Hide" : "View"}
                    </button>
                  </div>
                  {expandedRequestId === request.id && (
                    <div className="border-t bg-slate-50/70 p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold">{request.purpose}</p>
                          <p className="text-xs text-slate-500">
                            {request.request_number} ·{" "}
                            {dateLabel(request.travel_date)}
                            {request.return_date
                              ? ` → ${dateLabel(request.return_date)}`
                              : ""}
                          </p>
                        </div>
                        <Badge className={statusClass(request.status)}>
                          {titleCase(request.status)}
                        </Badge>
                      </div>
                      <p className="mt-2 text-sm text-slate-600">
                        {request.origin} → {request.destination} ·{" "}
                        {request.passengers} passenger
                        {request.passengers === 1 ? "" : "s"}
                      </p>
                      {request.approval_document_name && (
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                          <p className="font-medium text-indigo-700">
                            Approval attached: {request.approval_document_name}
                          </p>
                          {request.approval_document_path && (
                            <button
                              type="button"
                              onClick={() => viewApprovalDocument(request)}
                              className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-white px-2 py-1 font-semibold text-indigo-700 hover:bg-indigo-50"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              View
                            </button>
                          )}
                        </div>
                      )}
                      {request.trip_number && (
                        <p className="mt-1 text-xs text-indigo-700">
                          Trip assigned: {request.trip_number}
                        </p>
                      )}
                      {request.trip_id && (
                        <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50 p-3 text-xs text-indigo-900">
                          <p className="font-semibold">Dispatch assignment</p>
                          <div className="mt-2 grid gap-1 sm:grid-cols-2">
                            <span>
                              <strong>Vehicle:</strong>{" "}
                              {request.assigned_fleet_number || "Assigned"}
                              {request.assigned_vehicle
                                ? ` · ${request.assigned_vehicle}`
                                : ""}
                            </span>
                            <span>
                              <strong>Registration:</strong>{" "}
                              {request.assigned_registration_number || "—"}
                            </span>
                            <span>
                              <strong>Driver:</strong>{" "}
                              {request.assigned_driver_name ||
                                "Assigned Driver"}
                            </span>
                            <span>
                              <strong>Departure:</strong>{" "}
                              {dateTimeLabel(request.assigned_departure_at)}
                            </span>
                          </div>
                          {request.assigned_driver_email && (
                            <p className="mt-1">
                              Driver contact: {request.assigned_driver_email}
                            </p>
                          )}
                          {request.assigned_return_at && (
                            <p className="mt-1">
                              Return:{" "}
                              {dateTimeLabel(request.assigned_return_at)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {!dashboard.requests.length && (
                <EmptyState icon={ClipboardList} title="No requests submitted">
                  Use the form to request an institutional vehicle.
                </EmptyState>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderReports = () => (
    <div className="space-y-6">
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <SectionTitle
          icon={FileText}
          title="Reports & integrations"
          description="Download operational records and connect transport data to institutional systems."
          action={
            <button
              type="button"
              onClick={downloadReport}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground"
            >
              <FileText className="h-4 w-4" />
              Download fleet CSV
            </button>
          }
        />
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <CapabilityCard
            icon={BarChart3}
            title="Fleet performance"
            detail="Utilisation, downtime, mileage and total cost of ownership reports."
          />
          <CapabilityCard
            icon={Fuel}
            title="Fuel & emissions"
            detail="Litres per kilometre, idle time, anomalies and carbon footprint."
            tone="emerald"
          />
          <CapabilityCard
            icon={Globe2}
            title="Open API"
            detail="ERP, HR, payroll, accounting, fuel cards and dispatch integrations."
            tone="violet"
          />
          <CapabilityCard
            icon={Thermometer}
            title="IoT telemetry"
            detail="Temperature, humidity, tyre pressure and cargo status sensor feeds."
            tone="amber"
          />
        </div>
      </section>
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <SectionTitle
          icon={CloudCog}
          title="Integration health"
          description="Connect providers when credentials and devices are available."
        />
        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          <div className="rounded-lg border p-4">
            <p className="font-semibold">GPS / telematics provider</p>
            <p className="mt-1 text-xs text-slate-500">
              API endpoint and webhook receiver for live location.
            </p>
            <Badge className="mt-3 border-amber-200 bg-amber-50 text-amber-700">
              Not connected
            </Badge>
          </div>
          <div className="rounded-lg border p-4">
            <p className="font-semibold">ERP & finance</p>
            <p className="mt-1 text-xs text-slate-500">
              Fuel cards, purchase orders and cost allocation.
            </p>
            <Badge className="mt-3 border-blue-200 bg-blue-50 text-blue-700">
              Ready for credentials
            </Badge>
          </div>
          <div className="rounded-lg border p-4">
            <p className="font-semibold">IoT / dashcams</p>
            <p className="mt-1 text-xs text-slate-500">
              Sensor readings and safety video evidence.
            </p>
            <Badge className="mt-3 border-violet-200 bg-violet-50 text-violet-700">
              API ready
            </Badge>
          </div>
        </div>
      </section>
    </div>
  );

  if (!databaseReady && !loading) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Transport data is not connected</p>
            <p className="mt-1">
              Run{" "}
              <code className="rounded bg-white px-1.5 py-0.5 text-xs">
                20260917000006_transport_fleet_management.sql
              </code>{" "}
              plus{" "}
              <code className="rounded bg-white px-1.5 py-0.5 text-xs">
                20260917000009_transport_request_dates_and_approvals.sql
              </code>
              ,
              <code className="rounded bg-white px-1.5 py-0.5 text-xs">
                20260917000010_transport_driver_role.sql
              </code>{" "}
              and
              <code className="rounded bg-white px-1.5 py-0.5 text-xs">
                20260917000011_transport_request_assignment.sql
              </code>{" "}
              plus{" "}
              <code className="rounded bg-white px-1.5 py-0.5 text-xs">
                20260917000012_transport_request_assignment_details.sql
              </code>{" "}
              plus{" "}
              <code className="rounded bg-white px-1.5 py-0.5 text-xs">
                20260918000001_transport_release_expired_assignments.sql
              </code>{" "}
              in Supabase, then refresh this workspace.
            </p>
            <button
              type="button"
              onClick={load}
              className="mt-3 inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold"
            >
              <RefreshCw className="h-4 w-4" />
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading)
    return (
      <div className="grid min-h-56 place-items-center rounded-xl border bg-white text-slate-500">
        <RefreshCw className="h-5 w-5 animate-spin" />
      </div>
    );

  const renderers = {
    overview: renderOverview,
    fleet: renderFleet,
    dispatch: renderDispatch,
    maintenance: renderMaintenance,
    safety: renderSafety,
    fuel: renderFuel,
    requests: renderRequests,
    reports: renderReports,
  };
  return (
    <>
      {renderers[view]?.() || renderOverview()}
      <ApprovalDocumentPreview
        preview={approvalPreview}
        onClose={() => setApprovalPreview(null)}
      />
    </>
  );
};

const ApprovalDocumentPreview = ({ preview, onClose }) => {
  if (!preview) return null;
  const isImage = String(preview.mimeType || "").startsWith("image/");

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-4 border-b px-5 py-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
              Approval document
            </p>
            <p className="truncate text-sm font-semibold text-slate-900">
              {preview.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close document preview"
            className="rounded-lg border p-2 text-slate-600 hover:bg-slate-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-[55vh] overflow-auto bg-slate-100 p-3">
          {preview.loading ? (
            <div className="grid min-h-[55vh] place-items-center text-sm text-slate-500">
              Loading document…
            </div>
          ) : isImage ? (
            <div className="grid min-h-[55vh] place-items-center">
              <img
                src={preview.url}
                alt={preview.name}
                className="max-h-[72vh] max-w-full rounded-lg object-contain shadow"
              />
            </div>
          ) : (
            <iframe
              title={preview.name}
              src={preview.url}
              className="h-[72vh] min-h-[520px] w-full rounded-lg border bg-white"
            />
          )}
        </div>
        {!preview.loading && preview.url && (
          <div className="flex justify-end gap-2 border-t px-5 py-3">
            <a
              href={preview.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg border border-indigo-200 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
            >
              Open in new tab
            </a>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ClockIcon = ({ className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={className}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

export { TransportWorkspace, transportSections };
export default TransportWorkspace;
